import { listAiMessagesQuery } from "@domain/ai_answers"
import { useInfiniteQuery } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import {
  AiMessages,
  AiMessageStatusEnum,
  ChannelType
} from "__generated__/graphql"
import dayjs from "dayjs"
import { useMemo } from "react"

/**
 * The open thread's turns, oldest first. The page owns it (not the thread view) because the steps
 * panel walks the same list for previous/next and resolves a `?run=` link against it.
 */
export default function useAiConversationMessages(
  selectedItem: AiMessages | null
) {
  const query = useInfiniteQuery({
    // Thread key shape is matched by aiMessagesRealtime.utils — keep it in sync.
    queryKey: [
      "ai-message-list",
      selectedItem?.customerId,
      selectedItem?.conversationId
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await listAiMessagesQuery({
        customerId: selectedItem?.customerId || "",
        conversationId: selectedItem?.conversationId?._id || undefined,
        limit: 20,
        offset: pageParam
      })
      if (!response.isSuccess || !response.data) {
        showErrorToast(response.errorMessage ?? "Bir hata oluştu")
        return {
          docs: [],
          hasNextPage: false,
          totalDocs: 0
        }
      }
      return response.data
    },
    enabled: Boolean(
      selectedItem?.customerId && selectedItem?.conversationId?._id
    ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasNextPage) return undefined
      const count = allPages.reduce((acc, p) => acc + (p.docs?.length ?? 0), 0)
      return count
    },
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    placeholderData: undefined,
    staleTime: 0
  })

  const { data } = query
  const messages = useMemo<AiMessages[]>(() => {
    if (!data?.pages?.length) return []

    const flat = data.pages.flatMap((p) =>
      (p.docs ?? [])
        .filter((m) => m?._id)
        .map((m) => ({
          _id: m?._id ?? "",
          customerId: m?.customerId ?? "",
          conversationId: m?.conversationId ?? undefined,
          answerId: m?.answerId ?? "",
          questionId: m?.questionId ?? "",
          question: m?.question ?? "",
          answer: m?.answer ?? "",
          answers: m?.answers ?? undefined,
          status: m?.status ?? AiMessageStatusEnum.Failed,
          channelType: m?.channelType as ChannelType | null,
          error: m?.error,
          createdAt: m?.createdAt as Date,
          updatedAt: m?.updatedAt as Date,
          questionMedia: m?.questionMedia,
          questionContext: m?.questionContext,
          durationMs: m?.durationMs,
          usage: m?.usage,
          failedStepCount: m?.failedStepCount,
          kind: m?.kind,
          outcome: m?.outcome
        }))
    )

    return flat.sort(
      (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
    )
  }, [data])

  return { ...query, messages }
}

export type AiConversationMessages = ReturnType<
  typeof useAiConversationMessages
>
